import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPlus, 
    faEdit, 
    faEye, 
    faCheck, 
    faUpload, 
    faDownload,
    faExclamationTriangle,
    faUser,
    faFileAlt,
    faImage,
    faTrash,
    faSpinner,
    faCheckCircle,
    faClockRotateLeft
} from '@fortawesome/free-solid-svg-icons';
import { useAlert } from '../../providers/AlertProvider';
import { useAuthProvider } from '../../providers/AuthProvider';
import { ActivityIndicator } from './ActivityIndicator';
import Button from './Button';
import TachoPDFViewer from './TachoPDFViewer';

import {
    getDrivers,
    getInfringements,
    getDriverInfringements,
    createInfringement,
    getInfringementDetails,
    updateInfringementStatus,
    updateInfringement,
    deleteInfringement,
    getInfringementStats,
    downloadInfringementFile,
    getInfringementFile,
    downloadSignedPDF,
    downloadSignedPDFs,
    downloadPrintout,
    validateInfringementData,
    getSeverityColor as apiGetSeverityColor,
    getStatusColor as apiGetStatusColor,
    formatInfringementDate,
    formatInfringementDateTime
} from '../../api/tacho';

/**
 * TachoManagement Component
 * 
 * Manages digital tacho infringements:
 * - View all drivers and admin accounts
 * - Upload tacho infringement PDFs
 * - Track pending and signed-off infringements 
 * - Handle printout requirements
 * - Mobile signature integration (placeholder for future development)
 */
const TachoManagement = ({ 
    theme = "light",
    isLoading = false,
    isAdmin = false,
    profile = null
}) => {
    const { showAlert } = useAlert();
    const { permissions, isDev } = useAuthProvider();
    const canEdit = isDev || (permissions && permissions.perm_tacho_edit);
    const [activeView, setActiveView] = useState('overview');
    const [infringements, setInfringements] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [selectedInfringement, setSelectedInfringement] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
    const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    const [currentImageUrl, setCurrentImageUrl] = useState(null);
    const [imageViewerType, setImageViewerType] = useState('signature'); // 'signature' or 'printout'
    
    // Driver-specific infringements view state
    const [selectedDriverForInfringements, setSelectedDriverForInfringements] = useState(null);
    const [driverInfringements, setDriverInfringements] = useState([]);
    
    // New infringement form state
    const [newInfringement, setNewInfringement] = useState({
        profile_id: '',
        infringement_date: '',
        infringement_type: 'driving_time',
        severity: 'minor',
        description: '',
        vehicle_registration: '',
        printout_required: false
    });
    
    // File upload states
    const [pdfFile, setPdfFile] = useState(null);

    // Theme colors
    const isDark = theme === "dark";
    const colors = {
        bg: isDark ? "#080808" : "#fff",
        panel: isDark ? "#333" : "#fff",
        fg: isDark ? "#fff" : "#000",
        border: isDark ? "#fff" : "#000",
        muted: isDark ? "#ccc" : "#666",
        success: "#28a745",
        danger: "#dc3545",
        warning: "#ffc107",
        info: "#17a2b8",
        blue: "#1997eb"
    };

    // Fetch data functions
    const fetchInfringements = useCallback(async () => {
        setLoading(true);
        try {
            console.log('🔍 Fetching infringements...');
            const result = await getInfringements();
            console.log('📊 API Result:', result);
            
            if (result.success) {
                // Filter for pending infringements + signed-off (not yet downloaded) + any infringements from last 28 days
                const twentyEightDaysAgo = new Date();
                twentyEightDaysAgo.setDate(twentyEightDaysAgo.getDate() - 28);
                
                const filteredInfringements = result.infringements.filter(inf => {
                    // Always include pending infringements
                    if (inf.status === 'pending') return true;
                    
                    // Always include non-pending, not-downloaded infringements (Signed Off tab)
                    if (inf.downloaded === false || inf.downloaded == null) return true;
                    
                    // Include any infringement from last 28 days
                    const infringementDate = new Date(inf.infringement_date);
                    return infringementDate >= twentyEightDaysAgo;
                });
                
                setInfringements(filteredInfringements);
                console.log('✅ Infringements loaded:', filteredInfringements.length);
            } else {
                console.error('❌ API Error:', result.error);
                showAlert({
                    message: result.error || 'Failed to fetch infringements',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            console.error('💥 Network Error:', error);
            showAlert({
                message: `Network error: ${error.message}`,
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setLoading(false);
        }
    }, []); // Remove showAlert dependency to prevent infinite loop

    const fetchDrivers = useCallback(async () => {
        try {
            console.log('🔍 Fetching drivers...');
            const result = await getDrivers();
            console.log('👥 Drivers Result:', result);
            
            if (result.success) {
                setDrivers(result.drivers);
                console.log('✅ Drivers loaded:', result.drivers.length);
            } else {
                console.error('❌ Drivers Error:', result.error);
            }
        } catch (error) {
            console.error('💥 Drivers Network Error:', error);
        }
    }, []); // Remove showAlert dependency to prevent infinite loop

    const fetchDriverInfringements = useCallback(async (driverId) => {
        setLoading(true);
        try {
            console.log('🔍 Fetching infringements for driver ID:', driverId);
            const result = await getDriverInfringements(driverId);
            console.log('📊 Driver Infringements Result:', result);
            
            if (result.success) {
                setDriverInfringements(result.infringements);
                console.log('✅ Driver infringements loaded:', result.infringements.length);
            } else {
                console.error('❌ Driver Infringements Error:', result.error);
                showAlert({
                    message: result.error || 'Failed to fetch driver infringements',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            console.error('💥 Driver Infringements Network Error:', error);
            showAlert({
                message: `Network error: ${error.message}`,
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setLoading(false);
        }
    }, [showAlert]);

    const handleViewDriverInfringements = (driver) => {
        setSelectedDriverForInfringements(driver);
        setActiveView('driver_infringements');
        fetchDriverInfringements(driver.id);
    };

    const handleBackFromDriverView = () => {
        setSelectedDriverForInfringements(null);
        setDriverInfringements([]);
        setActiveView('drivers');
    };

    // Fetch data on component mount
    useEffect(() => {
        console.log('🚀 TachoManagement useEffect triggered, isAdmin:', isAdmin);
        if (isAdmin) {
            fetchInfringements();
            fetchDrivers();
        }
    }, [isAdmin]); // Remove function dependencies to prevent infinite loop

    const handleCreateInfringement = async () => {
        // Validate form data
        const validation = validateInfringementData(newInfringement);
        if (!validation.isValid) {
            const errorMessages = Object.values(validation.errors).join(', ');
            showAlert({
                message: `Please fix the following errors: ${errorMessages}`,
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            return;
        }

        setLoading(true);
        try {
            const result = await createInfringement(newInfringement, pdfFile, null);

            if (result.success) {
                showAlert({
                    message: result.message || 'Infringement created successfully',
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
                setUploadModalOpen(false);
                resetForm();
                fetchInfringements();
            } else {
                showAlert({
                    message: result.error || 'Failed to create infringement',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            console.error('Error creating infringement:', error);
            showAlert({
                message: 'Failed to create infringement',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setNewInfringement({
            profile_id: '',
            infringement_date: '',
            infringement_type: 'driving_time',
            severity: 'minor',
            description: '',
            vehicle_registration: '',
            printout_required: false
        });
        setPdfFile(null);
    };

    const getSeverityColor = (severity) => {
        return apiGetSeverityColor(severity) || colors.muted;
    };

    const getStatusColor = (status) => {
        return apiGetStatusColor(status) || colors.muted;
    };

    const formatDate = (dateString) => {
        return formatInfringementDate(dateString);
    };

    const formatDateTime = (dateString) => {
        return formatInfringementDateTime(dateString);
    };

    const formatCurrency = (amount) => {
        return amount ? `£${parseFloat(amount).toFixed(2)}` : 'N/A';
    };

    // Filter infringements
    const pendingInfringements = infringements.filter(inf => inf.status === 'pending');
    const signedOffInfringements = infringements.filter(inf => {
        console.log(`🔍 Infringement ${inf.id}: status=${inf.status}, downloaded=${inf.downloaded}, type=${typeof inf.downloaded}`);
        // Show non-pending infringements that are NOT downloaded (false, null, or undefined)
        return inf.status !== 'pending' && (inf.downloaded === false || inf.downloaded == null);
    });
    const disputedInfringements = infringements.filter(inf => inf.status === 'disputed');

    const handleDownloadAllSigned = async () => {
        setLoading(true);
        try {
            const infringementIds = signedOffInfringements.map(infringement => infringement.id);
            const downloadResult = await downloadSignedPDFs(infringementIds);

            if (!downloadResult.success) {
                showAlert({
                    message: downloadResult.error || 'Failed to download signed infringements',
                    type: 'danger',
                    buttons: [{ label: 'OK', type: 'danger' }]
                });
                return;
            }

            const updateResults = await Promise.all(
                infringementIds.map(id => updateInfringement(id, { downloaded: true }))
            );
            const failedUpdates = updateResults.filter(result => !result.success);

            await fetchInfringements();
            showAlert({
                message: failedUpdates.length === 0
                    ? `${infringementIds.length} signed infringements downloaded successfully.`
                    : `The archive downloaded, but ${failedUpdates.length} infringement status${failedUpdates.length === 1 ? '' : 'es'} could not be updated.`,
                type: failedUpdates.length === 0 ? 'success' : 'warning',
                buttons: [{
                    label: 'OK',
                    type: failedUpdates.length === 0 ? 'success' : 'warning'
                }]
            });
        } catch (error) {
            console.error('💥 Bulk Download Error:', error);
            showAlert({
                message: `Error downloading signed infringements: ${error.message}`,
                type: 'danger',
                buttons: [{ label: 'OK', type: 'danger' }]
            });
        } finally {
            setLoading(false);
        }
    };
    
    // Get infringements from last 28 days (for display count)
    const twentyEightDaysAgo = new Date();
    twentyEightDaysAgo.setDate(twentyEightDaysAgo.getDate() - 28);
    const last28DaysInfringements = infringements.filter(inf => {
        const infringementDate = new Date(inf.infringement_date);
        return infringementDate >= twentyEightDaysAgo;
    });
    
    // Sort infringements for overview: pending first (alphabetically by name, then by date), then others (alphabetically by name, then by date)
    const sortedOverviewInfringements = [...infringements].sort((a, b) => {
        // Group by pending status first
        if (a.status === 'pending' && b.status !== 'pending') return -1;
        if (a.status !== 'pending' && b.status === 'pending') return 1;
        
        // Within each group, sort alphabetically by driver name
        const nameA = `${a.driver_name} ${a.driver_surname}`.toLowerCase();
        const nameB = `${b.driver_name} ${b.driver_surname}`.toLowerCase();
        const nameComparison = nameA.localeCompare(nameB);
        
        // If names are the same, sort by date (most recent first)
        if (nameComparison === 0) {
            const dateA = new Date(a.infringement_date);
            const dateB = new Date(b.infringement_date);
            return dateB - dateA; // Most recent first
        }
        
        return nameComparison;
    });

    if (!isAdmin) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <p style={{ color: colors.danger, fontSize: '18px', fontWeight: 'bold' }}>
                    Admin Access Required
                </p>
                <p style={{ color: colors.muted }}>
                    You need administrator privileges to access the Tacho Management system.
                </p>
            </div>
        );
    }

    if (loading) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <ActivityIndicator />
                <p style={{ color: colors.muted, marginTop: '20px' }}>Loading tacho data...</p>
            </div>
        );
    }

    return (
        <div style={{ 
            background: colors.bg, 
            color: colors.fg, 
            padding: '20px',
            minHeight: '100vh'
        }}>
            {/* Header */}
            <div style={{ 
                textAlign: 'center', 
                marginBottom: '30px',
                padding: '20px',
                background: `linear-gradient(135deg, ${colors.blue}20, ${colors.blue}10)`,
                borderRadius: '10px',
                border: `1px solid ${colors.border}20`
            }}>
                <h2 style={{ 
                    color: colors.blue, 
                    marginBottom: '10px',
                    fontSize: '32px',
                    fontWeight: 'bold'
                }}>
                    🚛 Tacho Infringement Management
                </h2>
                <p style={{ color: colors.muted, fontSize: '16px' }}>
                    Digital management of tachograph infringements and driver compliance
                </p>
            </div>

            {/* Navigation Tabs */}
            <div style={{ 
                display: 'flex', 
                gap: '10px', 
                marginBottom: '30px',
                flexWrap: 'wrap',
                justifyContent: 'center'
            }}>
                {[
                    { key: 'overview', label: '📊 Overview', count: infringements.length },
                    { key: 'pending', label: '⏳ Pending', count: pendingInfringements.length },
                    { key: 'signed', label: '✅ Signed Off', count: signedOffInfringements.length },
                    { key: 'drivers', label: '👥 Drivers', count: drivers.length }
                ].map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => {
                            setActiveView(tab.key);
                            // Reset driver-specific state when navigating away
                            if (activeView === 'driver_infringements') {
                                setSelectedDriverForInfringements(null);
                                setDriverInfringements([]);
                            }
                        }}
                        style={{
                            padding: '12px 20px',
                            border: `2px solid ${activeView === tab.key ? colors.blue : colors.border}20`,
                            borderRadius: '8px',
                            background: activeView === tab.key ? colors.blue + '20' : colors.panel,
                            color: colors.fg,
                            cursor: 'pointer',
                            fontWeight: activeView === tab.key ? 'bold' : 'normal',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        {tab.label}
                        {tab.count > 0 && (
                            <span style={{
                                background: activeView === tab.key ? colors.blue : colors.muted,
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 'bold'
                            }}>
                                {tab.count}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Create New Infringement Button */}
            {canEdit && (
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <Button
                        onClick={() => setUploadModalOpen(true)}
                        style={{
                            background: colors.success,
                            color: 'white',
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            margin: '0 auto'
                        }}
                    >
                        <FontAwesomeIcon icon={faPlus} style={{ width: 30 }} />
                        Record New Infringement
                    </Button>
                </div>
            )}

            {/* Content based on active view */}
            {activeView === 'overview' && (
                <div>
                    <h3 style={{ color: colors.blue, marginBottom: '20px' }}>📊 System Overview</h3>
                    
                    {/* Stats Cards */}
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                        gap: '20px',
                        marginBottom: '30px'
                    }}>
                        <div style={{
                            background: colors.panel,
                            padding: '20px',
                            borderRadius: '12px',
                            border: `1px solid ${colors.border}20`,
                            textAlign: 'center'
                        }}>
                            <h4 style={{ color: colors.warning, marginBottom: '10px' }}>⚠️ Pending</h4>
                            <h2 style={{ color: colors.fg, margin: '0', fontSize: '36px' }}>
                                {pendingInfringements.length}
                            </h2>
                        </div>
                        
                        <div style={{
                            background: colors.panel,
                            padding: '20px',
                            borderRadius: '12px',
                            border: `1px solid ${colors.border}20`,
                            textAlign: 'center'
                        }}>
                            <h4 style={{ color: colors.success, marginBottom: '10px' }}>✅ Signed Off</h4>
                            <h2 style={{ color: colors.fg, margin: '0', fontSize: '36px' }}>
                                {signedOffInfringements.length}
                            </h2>
                        </div>
                        
                        <div style={{
                            background: colors.panel,
                            padding: '20px',
                            borderRadius: '12px',
                            border: `1px solid ${colors.border}20`,
                            textAlign: 'center'
                        }}>
                            <h4 style={{ color: colors.info, marginBottom: '10px' }}>👥 Active Drivers</h4>
                            <h2 style={{ color: colors.fg, margin: '0', fontSize: '36px' }}>
                                {drivers.length}
                            </h2>
                        </div>
                        
                        <div style={{
                            background: colors.panel,
                            padding: '20px',
                            borderRadius: '12px',
                            border: `1px solid ${colors.border}20`,
                            textAlign: 'center'
                        }}>
                            <h4 style={{ color: colors.info, marginBottom: '10px' }}>📅 Last 28 Days</h4>
                            <h2 style={{ color: colors.fg, margin: '0', fontSize: '36px' }}>
                                {last28DaysInfringements.length}
                            </h2>
                        </div>
                    </div>
                    
                    {/* Recent Infringements Table */}
                    <div style={{
                        background: colors.panel,
                        padding: '20px',
                        borderRadius: '12px',
                        border: `1px solid ${colors.border}20`
                    }}>
                        <h4 style={{ color: colors.blue, marginBottom: '20px' }}>
                            📋 Recent Infringements
                        </h4>
                        
                        {infringements.length === 0 ? (
                            <p style={{ 
                                textAlign: 'center', 
                                color: colors.muted, 
                                padding: '40px',
                                fontSize: '16px'
                            }}>
                                No infringements recorded yet. Click "Record New Infringement" to get started.
                            </p>
                        ) : (
                            <div style={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                                gap: '20px'
                            }}>
                                {sortedOverviewInfringements.slice(0, 10).map((infringement) => (
                                    <div key={infringement.id} style={{
                                        background: colors.bg,
                                        border: `2px solid ${colors.border}20`,
                                        borderRadius: '12px',
                                        padding: '20px',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        position: 'relative'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                        e.currentTarget.style.boxShadow = `0 8px 16px ${colors.border}40`;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                            <div style={{ flex: 1 }}>
                                                <h4 style={{ color: colors.blue, margin: '0 0 8px 0', fontSize: '16px' }}>
                                                    {infringement.driver_name} {infringement.driver_surname}
                                                </h4>
                                                <p style={{ color: colors.muted, margin: '0', fontSize: '13px' }}>
                                                    {formatDate(infringement.infringement_date)}
                                                </p>
                                            </div>
                                            <span style={{
                                                background: getStatusColor(infringement.status) + '20',
                                                color: getStatusColor(infringement.status),
                                                padding: '4px 12px',
                                                borderRadius: '12px',
                                                fontSize: '11px',
                                                fontWeight: 'bold',
                                                textTransform: 'uppercase'
                                            }}>
                                                {infringement.status.replace('_', ' ')}
                                            </span>
                                        </div>
                                        
                                        <div style={{ margin: '16px 0' }}>
                                            <p style={{ color: colors.fg, margin: '8px 0', fontSize: '14px' }}>
                                                <strong>Type:</strong> {infringement.infringement_type.replace('_', ' ')}
                                            </p>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                                                <span style={{ color: colors.muted, fontSize: '13px' }}>Severity:</span>
                                                <span style={{
                                                    color: getSeverityColor(infringement.severity),
                                                    fontWeight: 'bold',
                                                    fontSize: '14px',
                                                    textTransform: 'capitalize'
                                                }}>
                                                    {infringement.severity.replace('_', ' ')}
                                                </span>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedInfringement(infringement);
                                                }}
                                                style={{
                                                    flex: 1,
                                                    background: colors.info,
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '10px',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    fontWeight: '500',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faEye} style={{ width: 30 }} /> View Details
                                            </button>
                                            {canEdit && infringement.status === 'pending' && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        showAlert({
                                                            message: `Are you sure you want to delete this infringement for ${infringement.driver_name} ${infringement.driver_surname}?`,
                                                            type: 'danger',
                                                            buttons: [
                                                                { 
                                                                    label: 'Delete', 
                                                                    type: 'danger',
                                                                    onClick: async () => {
                                                                        try {
                                                                            setLoading(true);
                                                                            const response = await deleteInfringement(infringement.id);
                                                                            if (response.success) {
                                                                                await fetchInfringements();
                                                                                showAlert({
                                                                                    message: 'Infringement deleted successfully',
                                                                                    type: 'success',
                                                                                    buttons: [{ label: 'Close', type: 'primary' }]
                                                                                });
                                                                            } else {
                                                                                showAlert({
                                                                                    message: response.error || 'Failed to delete infringement',
                                                                                    type: 'danger',
                                                                                    buttons: [{ label: 'Close', type: 'primary' }]
                                                                                });
                                                                            }
                                                                        } catch (error) {
                                                                            showAlert({
                                                                                message: `Failed to delete infringement: ${error.message}`,
                                                                                type: 'danger',
                                                                                buttons: [{ label: 'Close', type: 'primary' }]
                                                                            });
                                                                        } finally {
                                                                            setLoading(false);
                                                                        }
                                                                    }
                                                                },
                                                                { label: 'Cancel', type: 'secondary' }
                                                            ]
                                                        });
                                                    }}
                                                    style={{
                                                        background: colors.danger,
                                                        color: 'white',
                                                        border: 'none',
                                                        padding: '10px 12px',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontSize: '13px'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} style={{ width: 30 }} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeView === 'pending' && (
                <div style={{
                    background: colors.panel,
                    padding: '20px',
                    borderRadius: '12px',
                    border: `1px solid ${colors.border}20`
                }}>
                    <h3 style={{ color: colors.warning, marginBottom: '20px' }}>⏳ Pending Infringements</h3>
                    
                    {pendingInfringements.length === 0 ? (
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '40px', 
                            color: colors.muted 
                        }}>
                            <FontAwesomeIcon icon={faCheckCircle} size="3x" style={{ width: 30, marginBottom: '20px' }} />
                            <p style={{ fontSize: '18px' }}>No pending infringements</p>
                            <p style={{ fontSize: '14px' }}>All infringements have been signed off.</p>
                        </div>
                    ) : (
                        <div style={{ 
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                            gap: '20px'
                        }}>
                            {pendingInfringements.map((infringement) => (
                                <div key={infringement.id} style={{
                                    background: colors.bg,
                                    border: `2px solid ${colors.warning}30`,
                                    borderRadius: '12px',
                                    padding: '20px',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer',
                                    position: 'relative'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = `0 8px 16px ${colors.warning}40`;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                        <div style={{ flex: 1 }}>
                                            <h4 style={{ color: colors.blue, margin: '0 0 8px 0', fontSize: '16px' }}>
                                                {infringement.driver_name} {infringement.driver_surname}
                                            </h4>
                                            <p style={{ color: colors.muted, margin: '0', fontSize: '13px' }}>
                                                {formatDate(infringement.infringement_date)}
                                            </p>
                                        </div>
                                        <span style={{
                                            background: colors.warning + '20',
                                            color: colors.warning,
                                            padding: '4px 12px',
                                            borderRadius: '12px',
                                            fontSize: '11px',
                                            fontWeight: 'bold',
                                            textTransform: 'uppercase'
                                        }}>
                                            Pending
                                        </span>
                                    </div>
                                    
                                    <div style={{ margin: '16px 0' }}>
                                        <p style={{ color: colors.fg, margin: '8px 0', fontSize: '14px' }}>
                                            <strong>Type:</strong> {infringement.infringement_type.replace('_', ' ')}
                                        </p>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                                            <span style={{ color: colors.muted, fontSize: '13px' }}>Severity:</span>
                                            <span style={{
                                                color: getSeverityColor(infringement.severity),
                                                fontWeight: 'bold',
                                                fontSize: '14px',
                                                textTransform: 'capitalize'
                                            }}>
                                                {infringement.severity.replace('_', ' ')}
                                            </span>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedInfringement(infringement);
                                            }}
                                            style={{
                                                flex: 1,
                                                background: colors.info,
                                                color: 'white',
                                                border: 'none',
                                                padding: '10px',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                fontWeight: '500',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faEye} style={{ width: 30 }} /> View Details
                                        </button>
                                        {canEdit && infringement.status === 'pending' && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    showAlert({
                                                        message: `Are you sure you want to delete this infringement for ${infringement.driver_name} ${infringement.driver_surname}?`,
                                                        type: 'danger',
                                                        buttons: [
                                                            { 
                                                                label: 'Delete', 
                                                                type: 'danger',
                                                                onClick: async () => {
                                                                    try {
                                                                        setLoading(true);
                                                                        const response = await deleteInfringement(infringement.id);
                                                                        if (response.success) {
                                                                            await fetchInfringements();
                                                                            showAlert({
                                                                                message: 'Infringement deleted successfully',
                                                                                type: 'success',
                                                                                buttons: [{ label: 'Close', type: 'primary' }]
                                                                            });
                                                                        } else {
                                                                            showAlert({
                                                                                message: response.error || 'Failed to delete infringement',
                                                                                type: 'danger',
                                                                                buttons: [{ label: 'Close', type: 'primary' }]
                                                                            });
                                                                        }
                                                                    } catch (error) {
                                                                        showAlert({
                                                                            message: `Failed to delete infringement: ${error.message}`,
                                                                            type: 'danger',
                                                                            buttons: [{ label: 'Close', type: 'primary' }]
                                                                        });
                                                                    } finally {
                                                                        setLoading(false);
                                                                    }
                                                                }
                                                            },
                                                            { label: 'Cancel', type: 'secondary' }
                                                        ]
                                                    });
                                                }}
                                                style={{
                                                    background: colors.danger,
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '10px 12px',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px'
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faTrash} style={{ width: 30 }} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeView === 'signed' && (
                <div style={{
                    background: colors.panel,
                    padding: '20px',
                    borderRadius: '12px',
                    border: `1px solid ${colors.border}20`
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '20px',
                        flexWrap: 'wrap'
                    }}>
                        <h3 style={{ color: colors.success, margin: 0 }}>✅ Signed Off Infringements</h3>
                        {canEdit && signedOffInfringements.length > 0 && (
                            <button
                                onClick={handleDownloadAllSigned}
                                style={{
                                    background: colors.success,
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 16px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <FontAwesomeIcon icon={faDownload} style={{ width: 16 }} />
                                Download All ({signedOffInfringements.length})
                            </button>
                        )}
                    </div>
                    
                    {signedOffInfringements.length === 0 ? (
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '40px', 
                            color: colors.muted 
                        }}>
                            <FontAwesomeIcon icon={faClockRotateLeft} size="3x" style={{ width: 30, marginBottom: '20px' }} />
                            <p style={{ fontSize: '18px' }}>No signed off infringements</p>
                            <p style={{ fontSize: '14px' }}>Completed infringements will appear here.</p>
                        </div>
                    ) : (
                        <div style={{ 
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                            gap: '20px'
                        }}>
                            {signedOffInfringements.map((infringement) => (
                                <div key={infringement.id} style={{
                                    background: colors.bg,
                                    border: `2px solid ${colors.success}30`,
                                    borderRadius: '12px',
                                    padding: '20px',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer',
                                    position: 'relative'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = `0 8px 16px ${colors.success}40`;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                        <div style={{ flex: 1 }}>
                                            <h4 style={{ color: colors.blue, margin: '0 0 8px 0', fontSize: '16px' }}>
                                                {infringement.driver_name} {infringement.driver_surname}
                                            </h4>
                                            <p style={{ color: colors.muted, margin: '0', fontSize: '13px' }}>
                                                {formatDate(infringement.infringement_date)}
                                            </p>
                                        </div>
                                        <span style={{
                                            background: colors.success + '20',
                                            color: colors.success,
                                            padding: '4px 12px',
                                            borderRadius: '12px',
                                            fontSize: '11px',
                                            fontWeight: 'bold',
                                            textTransform: 'uppercase'
                                        }}>
                                            <FontAwesomeIcon icon={faCheck} style={{ width: 30 }} /> Signed
                                        </span>
                                    </div>
                                    
                                    <div style={{ margin: '16px 0' }}>
                                        <p style={{ color: colors.fg, margin: '8px 0', fontSize: '14px' }}>
                                            <strong>Type:</strong> {infringement.infringement_type.replace('_', ' ')}
                                        </p>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                                            <span style={{ color: colors.muted, fontSize: '13px' }}>Severity:</span>
                                            <span style={{
                                                color: getSeverityColor(infringement.severity),
                                                fontWeight: 'bold',
                                                fontSize: '14px',
                                                textTransform: 'capitalize'
                                            }}>
                                                {infringement.severity.replace('_', ' ')}
                                            </span>
                                        </div>
                                        {infringement.signed_at && (
                                            <p style={{ color: colors.muted, margin: '8px 0 0 0', fontSize: '12px' }}>
                                                Signed: {formatDate(infringement.signed_at)}
                                            </p>
                                        )}
                                    </div>

                                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedInfringement(infringement);
                                            }}
                                            style={{
                                                flex: 1,
                                                background: colors.info,
                                                color: 'white',
                                                border: 'none',
                                                padding: '10px',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                fontWeight: '500',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faEye} style={{ width: 16 }} /> View
                                        </button>
                                        {canEdit && (
                                            <button
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    try {
                                                        setLoading(true);
                                                        console.log('📥 Downloading infringement:', infringement.id);
                                                        
                                                        // Download the PDF
                                                        const response = await downloadSignedPDF(infringement.id);
                                                        
                                                        if (response.success) {
                                                            // Update the downloaded status to true
                                                            const updateResult = await updateInfringement(infringement.id, { downloaded: true });
                                                            
                                                            if (updateResult.success) {
                                                                // Refresh the infringements to update the display
                                                                await fetchInfringements();
                                                                
                                                                showAlert({
                                                                    message: 'Infringement downloaded successfully and marked as downloaded!',
                                                                    type: 'success',
                                                                    buttons: [{ label: 'OK', type: 'success' }]
                                                                });
                                                            } else {
                                                                showAlert({
                                                                    message: 'Downloaded but failed to update status: ' + (updateResult.error || 'Unknown error'),
                                                                    type: 'warning',
                                                                    buttons: [{ label: 'OK', type: 'warning' }]
                                                                });
                                                            }
                                                        } else {
                                                            showAlert({
                                                                message: response.error || 'Failed to download infringement',
                                                                type: 'danger',
                                                                buttons: [{ label: 'OK', type: 'danger' }]
                                                            });
                                                        }
                                                    } catch (error) {
                                                        console.error('💥 Download Error:', error);
                                                        showAlert({
                                                            message: 'Error downloading infringement: ' + error.message,
                                                            type: 'danger',
                                                            buttons: [{ label: 'OK', type: 'danger' }]
                                                        });
                                                    } finally {
                                                        setLoading(false);
                                                    }
                                                }}
                                                style={{
                                                    flex: 1,
                                                    background: colors.success,
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '10px',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    fontWeight: '500',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faDownload} style={{ width: 16 }} /> Download
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeView === 'drivers' && (
                <div style={{
                    background: colors.panel,
                    padding: '20px',
                    borderRadius: '12px',
                    border: `1px solid ${colors.border}20`
                }}>
                    <h3 style={{ color: colors.blue, marginBottom: '20px' }}>👥 Driver Directory</h3>
                    
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                        gap: '20px' 
                    }}>
                        {drivers.map((driver) => {
                            const driverInfringements = infringements.filter(inf => inf.profile_id === driver.id);
                            const pendingCount = driverInfringements.filter(inf => inf.status === 'pending').length;
                            
                            return (
                                <div
                                    key={driver.id}
                                    style={{
                                        background: colors.bg,
                                        border: `1px solid ${colors.border}20`,
                                        borderRadius: '8px',
                                        padding: '16px',
                                        textAlign: 'center'
                                    }}
                                >
                                    <div style={{ marginBottom: '12px' }}>
                                        <FontAwesomeIcon 
                                            icon={faUser} 
                                            size="2x" 
                                            style={{ color: colors.blue, width: 30 }}
                                        />
                                    </div>
                                    
                                    <h4 style={{ 
                                        color: colors.fg, 
                                        marginBottom: '8px',
                                        fontSize: '18px'
                                    }}>
                                        {driver.name} {driver.surname}
                                    </h4>
                                    
                                    <p style={{ 
                                        color: colors.muted, 
                                        marginBottom: '12px',
                                        textTransform: 'capitalize',
                                        fontSize: '14px'
                                    }}>
                                        {driver.group}
                                    </p>
                                    
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-around',
                                        marginBottom: '12px'
                                    }}>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ 
                                                fontSize: '20px', 
                                                fontWeight: 'bold', 
                                                color: colors.fg 
                                            }}>
                                                {driverInfringements.length}
                                            </div>
                                            <div style={{ 
                                                fontSize: '12px', 
                                                color: colors.muted 
                                            }}>
                                                Total
                                            </div>
                                        </div>
                                        
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ 
                                                fontSize: '20px', 
                                                fontWeight: 'bold', 
                                                color: pendingCount > 0 ? colors.warning : colors.success
                                            }}>
                                                {pendingCount}
                                            </div>
                                            <div style={{ 
                                                fontSize: '12px', 
                                                color: colors.muted 
                                            }}>
                                                Pending
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Button container for both actions */}
                                    <div style={{ 
                                        display: 'flex', 
                                        gap: '8px',
                                        marginTop: '12px'
                                    }}>
                                        <button
                                            onClick={() => handleViewDriverInfringements(driver)}
                                            style={{
                                                background: colors.info,
                                                color: 'white',
                                                border: 'none',
                                                padding: '8px 12px',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '11px',
                                                flex: '1',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '4px'
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faClockRotateLeft} style={{ width: 30 }} /> 
                                            View History
                                        </button>
                                        
                                        {canEdit && (
                                            <button
                                                onClick={() => {
                                                    setNewInfringement(prev => ({
                                                        ...prev,
                                                        profile_id: driver.id
                                                    }));
                                                    setUploadModalOpen(true);
                                                }}
                                                style={{
                                                    background: colors.blue,
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '8px 12px',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '11px',
                                                    flex: '1',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '4px'
                                                }}
                                              >
                                                <FontAwesomeIcon icon={faPlus} style={{ width: 30 }} /> 
                                                Record New
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Driver Historic Infringements View */}
            {activeView === 'driver_infringements' && selectedDriverForInfringements && (
                <div style={{
                    background: colors.panel,
                    padding: '20px',
                    borderRadius: '12px',
                    border: `1px solid ${colors.border}20`
                }}>
                    {/* Header with back button */}
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        marginBottom: '20px',
                        gap: '15px'
                    }}>
                        <button
                            onClick={handleBackFromDriverView}
                            style={{
                                background: colors.muted,
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            ← Back to Drivers
                        </button>
                        
                        <div>
                            <h3 style={{ 
                                color: colors.blue, 
                                margin: '0',
                                fontSize: '24px'
                            }}>
                                🚛 {selectedDriverForInfringements.name} {selectedDriverForInfringements.surname}
                            </h3>
                            <p style={{ 
                                color: colors.muted, 
                                margin: '4px 0 0 0',
                                fontSize: '14px'
                            }}>
                                All Historic Infringements ({driverInfringements.length} total)
                            </p>
                        </div>
                    </div>
                    
                    {/* Driver Infringements List */}
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <ActivityIndicator />
                            <p style={{ color: colors.muted, marginTop: '20px' }}>
                                Loading driver infringements...
                            </p>
                        </div>
                    ) : driverInfringements.length === 0 ? (
                        <div style={{ 
                            textAlign: 'center', 
                            color: colors.muted, 
                            padding: '40px',
                            fontSize: '16px'
                        }}>
                            <FontAwesomeIcon 
                                icon={faCheckCircle} 
                                size="3x" 
                                style={{ width: 48, marginBottom: '20px', color: colors.success }} 
                            />
                            <p>
                                No infringements found for this driver. Great driving record! 🎉
                            </p>
                        </div>
                    ) : (
                        <div style={{ 
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                            gap: '20px'
                        }}>
                            {driverInfringements.map((infringement) => (
                                <div key={infringement.id} style={{
                                    background: colors.bg,
                                    border: `2px solid ${colors.border}20`,
                                    borderRadius: '12px',
                                    padding: '20px',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer'
                                }}
                                onClick={() => setSelectedInfringement(infringement)}
                                >
                                    <div style={{ marginBottom: '15px' }}>
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            marginBottom: '12px'
                                        }}>
                                            <span style={{
                                                background: getSeverityColor(infringement.severity) + '20',
                                                color: getSeverityColor(infringement.severity),
                                                padding: '4px 8px',
                                                borderRadius: '12px',
                                                fontSize: '11px',
                                                fontWeight: 'bold',
                                                textTransform: 'uppercase'
                                            }}>
                                                {infringement.severity}
                                            </span>
                                            
                                            <span style={{
                                                background: getStatusColor(infringement.status) + '20',
                                                color: getStatusColor(infringement.status),
                                                padding: '4px 8px',
                                                borderRadius: '12px',
                                                fontSize: '11px',
                                                fontWeight: 'bold',
                                                textTransform: 'capitalize'
                                            }}>
                                                {infringement.status.replace('_', ' ')}
                                            </span>
                                        </div>
                                        
                                        <h4 style={{ 
                                            color: colors.fg, 
                                            marginBottom: '8px',
                                            fontSize: '16px',
                                            lineHeight: '1.4'
                                        }}>
                                            {infringement.infringement_type
                                                ?.replace(/_/g, ' ')
                                                ?.replace(/\b\w/g, l => l.toUpperCase())
                                            }
                                        </h4>
                                        
                                        <p style={{ 
                                            color: colors.muted, 
                                            fontSize: '11px',
                                            marginBottom: '10px',
                                            lineHeight: '1.3'
                                        }}>
                                            📅 {formatDate(infringement.infringement_date)}
                                            {infringement.vehicle_registration && (
                                                <><br />🚚 {infringement.vehicle_registration}</>
                                            )}
                                        </p>
                                        
                                        {infringement.description && (
                                            <p style={{ 
                                                color: colors.fg, 
                                                fontSize: '12px',
                                                lineHeight: '1.4',
                                                marginBottom: '12px'
                                            }}>
                                                {infringement.description.length > 80 
                                                    ? `${infringement.description.substring(0, 80)}...` 
                                                    : infringement.description
                                                }
                                            </p>
                                        )}
                                    </div>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedInfringement(infringement);
                                        }}
                                        style={{
                                            width: '100%',
                                            background: colors.info,
                                            color: 'white',
                                            border: 'none',
                                            padding: '10px',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faEye} style={{ width: 30 }} /> View Details
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Create Infringement Modal */}
            {uploadModalOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '20px'
                }}>
                    <div style={{
                        background: colors.panel,
                        borderRadius: '12px',
                        padding: '30px',
                        maxWidth: '600px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        border: `1px solid ${colors.border}20`
                    }}>
                        <h3 style={{ color: colors.blue, marginBottom: '20px', textAlign: 'center' }}>
                            📝 Record New Tacho Infringement
                        </h3>
                        
                        <div style={{ display: 'grid', gap: '16px' }}>
                            {/* Driver Selection */}
                            <div>
                                <label style={{ 
                                    display: 'block', 
                                    marginBottom: '8px', 
                                    color: colors.fg,
                                    fontWeight: 'bold'
                                }}>
                                    Driver *
                                </label>
                                <select
                                    value={newInfringement.profile_id}
                                    onChange={(e) => setNewInfringement(prev => ({ ...prev, profile_id: e.target.value }))}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: `1px solid ${colors.border}40`,
                                        borderRadius: '4px',
                                        background: colors.bg,
                                        color: colors.fg,
                                        fontSize: '14px'
                                    }}
                                    required
                                >
                                    <option value="">Select Driver</option>
                                    {drivers.map(driver => (
                                        <option key={driver.id} value={driver.id}>
                                            {driver.name} {driver.surname} ({driver.group})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Infringement Date */}
                            <div>
                                <label style={{ 
                                    display: 'block', 
                                    marginBottom: '8px', 
                                    color: colors.fg,
                                    fontWeight: 'bold'
                                }}>
                                    Infringement Date *
                                </label>
                                <input
                                    type="date"
                                    value={newInfringement.infringement_date}
                                    onChange={(e) => setNewInfringement(prev => ({ ...prev, infringement_date: e.target.value }))}
                                    max={new Date().toISOString().split('T')[0]}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: `1px solid ${colors.border}40`,
                                        borderRadius: '4px',
                                        background: colors.bg,
                                        color: colors.fg,
                                        fontSize: '14px'
                                    }}
                                    required
                                />
                            </div>

                            {/* Two column layout for Type and Severity */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div>
                                    <label style={{ 
                                        display: 'block', 
                                        marginBottom: '8px', 
                                        color: colors.fg,
                                        fontWeight: 'bold'
                                    }}>
                                        Infringement Type *
                                    </label>
                                    <select
                                        value={newInfringement.infringement_type}
                                        onChange={(e) => setNewInfringement(prev => ({ ...prev, infringement_type: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            border: `1px solid ${colors.border}40`,
                                            borderRadius: '4px',
                                            background: colors.bg,
                                            color: colors.fg,
                                            fontSize: '14px'
                                        }}
                                        required
                                    >
                                        <option value="driving_time">Driving Time</option>
                                        <option value="rest_period">Daily Rest Period</option>
                                        <option value="weekly_rest">Weekly Rest</option>
                                        <option value="break_requirements">Break Requirements</option>
                                        <option value="card_not_inserted">Card Not Inserted</option>
                                        <option value="card_fault">Card Fault</option>
                                        <option value="manual_entry">Manual Entry</option>
                                        <option value="speed_violation">Speed Violation</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={{ 
                                        display: 'block', 
                                        marginBottom: '8px', 
                                        color: colors.fg,
                                        fontWeight: 'bold'
                                    }}>
                                        Severity *
                                    </label>
                                    <select
                                        value={newInfringement.severity}
                                        onChange={(e) => setNewInfringement(prev => ({ ...prev, severity: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            border: `1px solid ${colors.border}40`,
                                            borderRadius: '4px',
                                            background: colors.bg,
                                            color: colors.fg,
                                            fontSize: '14px'
                                        }}
                                        required
                                    >
                                        <option value="minor">Minor</option>
                                        <option value="serious">Serious</option>
                                        <option value="very_serious">Very Serious</option>
                                    </select>
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <label style={{ 
                                    display: 'block', 
                                    marginBottom: '8px', 
                                    color: colors.fg,
                                    fontWeight: 'bold'
                                }}>
                                    Description *
                                </label>
                                <textarea
                                    value={newInfringement.description}
                                    onChange={(e) => setNewInfringement(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Describe the infringement details..."
                                    rows="3"
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: `1px solid ${colors.border}40`,
                                        borderRadius: '4px',
                                        background: colors.bg,
                                        color: colors.fg,
                                        fontSize: '14px',
                                        resize: 'vertical'
                                    }}
                                    required
                                />
                            </div>

                            {/* Vehicle Registration */}
                            <div>
                                <label style={{ 
                                    display: 'block', 
                                    marginBottom: '8px', 
                                    color: colors.fg,
                                    fontWeight: 'bold'
                                }}>
                                    Vehicle Registration
                                </label>
                                <input
                                    type="text"
                                    value={newInfringement.vehicle_registration}
                                    onChange={(e) => setNewInfringement(prev => ({ ...prev, vehicle_registration: e.target.value.toUpperCase() }))}
                                    placeholder="e.g., AB12 CDE"
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: `1px solid ${colors.border}40`,
                                        borderRadius: '4px',
                                        background: colors.bg,
                                        color: colors.fg,
                                        fontSize: '14px'
                                    }}
                                />
                            </div>

                            {/* File Upload */}
                            <div>
                                <label style={{ 
                                    display: 'block', 
                                    marginBottom: '8px', 
                                    color: colors.fg,
                                    fontWeight: 'bold'
                                }}>
                                    <FontAwesomeIcon icon={faFileAlt} style={{ width: 30 }} /> PDF Document
                                </label>
                                <input
                                    type="file"
                                    accept=".pdf"
                                    onChange={(e) => setPdfFile(e.target.files[0])}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: `1px solid ${colors.border}40`,
                                        borderRadius: '4px',
                                        background: colors.bg,
                                        color: colors.fg,
                                        fontSize: '14px'
                                    }}
                                />
                            </div>

                            {/* Checkboxes */}
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                        type="checkbox"
                                        checked={newInfringement.printout_required}
                                        onChange={(e) => setNewInfringement(prev => ({ ...prev, printout_required: e.target.checked }))}
                                        style={{ transform: 'scale(1.2)' }}
                                    />
                                    <label style={{ color: colors.fg, fontSize: '14px' }}>
                                        Printout Required
                                    </label>
                                </div>
                            </div>



                            {/* Action Buttons */}
                            <div style={{ 
                                display: 'flex', 
                                gap: '12px', 
                                justifyContent: 'flex-end',
                                marginTop: '20px'
                            }}>
                                <Button
                                    onClick={() => {
                                        setUploadModalOpen(false);
                                        resetForm();
                                    }}
                                    style={{
                                        background: 'transparent',
                                        color: colors.fg,
                                        border: `1px solid ${colors.border}40`,
                                        padding: '10px 20px',
                                        borderRadius: '6px',
                                        fontSize: '14px'
                                    }}
                                >
                                    Cancel
                                </Button>
                                
                                <Button
                                    onClick={handleCreateInfringement}
                                    disabled={loading}
                                    style={{
                                        background: colors.success,
                                        color: 'white',
                                        border: 'none',
                                        padding: '10px 20px',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        opacity: loading ? 0.7 : 1
                                    }}
                                >
                                    {loading ? 'Creating...' : 'Create Infringement'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View Infringement Details Modal */}
            {selectedInfringement && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '20px'
                }}>
                    <div style={{
                        background: colors.panel,
                        borderRadius: '12px',
                        padding: '30px',
                        maxWidth: '700px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        border: `1px solid ${colors.border}20`
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ color: colors.blue, margin: 0 }}>
                                📋 Infringement Details
                            </h3>
                            <button
                                onClick={() => setSelectedInfringement(null)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: colors.fg,
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        
                        <div style={{ display: 'grid', gap: '16px' }}>
                            {/* Driver Information */}
                            <div style={{
                                background: colors.bg,
                                padding: '16px',
                                borderRadius: '8px',
                                border: `1px solid ${colors.border}20`
                            }}>
                                <h4 style={{ color: colors.blue, marginBottom: '12px' }}>👤 Driver Information</h4>
                                <p style={{ color: colors.fg, margin: '4px 0' }}>
                                    <strong>Name:</strong> {selectedInfringement.driver_name} {selectedInfringement.driver_surname}
                                </p>
                            </div>

                            {/* Infringement Details */}
                            <div style={{
                                background: colors.bg,
                                padding: '16px',
                                borderRadius: '8px',
                                border: `1px solid ${colors.border}20`
                            }}>
                                <h4 style={{ color: colors.blue, marginBottom: '12px' }}>⚠️ Infringement Details</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <p style={{ color: colors.fg, margin: '4px 0' }}>
                                        <strong>Date:</strong> {formatDate(selectedInfringement.infringement_date)}
                                    </p>
                                    <p style={{ color: colors.fg, margin: '4px 0' }}>
                                        <strong>Type:</strong> {selectedInfringement.infringement_type.replace('_', ' ')}
                                    </p>
                                    <p style={{ color: colors.fg, margin: '4px 0' }}>
                                        <strong>Severity:</strong> 
                                        <span style={{ 
                                            color: getSeverityColor(selectedInfringement.severity),
                                            fontWeight: 'bold',
                                            marginLeft: '8px',
                                            textTransform: 'capitalize'
                                        }}>
                                            {selectedInfringement.severity.replace('_', ' ')}
                                        </span>
                                    </p>
                                    <p style={{ color: colors.fg, margin: '4px 0' }}>
                                        <strong>Status:</strong>
                                        <span style={{ 
                                            color: getStatusColor(selectedInfringement.status),
                                            fontWeight: 'bold',
                                            marginLeft: '8px',
                                            textTransform: 'capitalize'
                                        }}>
                                            {selectedInfringement.status.replace('_', ' ')}
                                        </span>
                                    </p>
                                </div>
                                <p style={{ color: colors.fg, margin: '12px 0 4px 0' }}>
                                    <strong>Description:</strong>
                                </p>
                                <p style={{ color: colors.muted, margin: '4px 0', fontStyle: 'italic' }}>
                                    {selectedInfringement.description}
                                </p>
                                {selectedInfringement.vehicle_registration && (
                                    <p style={{ color: colors.fg, margin: '12px 0 4px 0' }}>
                                        <strong>Vehicle:</strong> {selectedInfringement.vehicle_registration}
                                    </p>
                                )}
                                {selectedInfringement.notes && (
                                    <>
                                        <p style={{ color: colors.fg, margin: '12px 0 4px 0' }}>
                                            <strong>Driver Notes:</strong>
                                        </p>
                                        <p style={{ color: colors.muted, margin: '4px 0', fontStyle: 'italic' }}>
                                            {selectedInfringement.notes}
                                        </p>
                                    </>
                                )}
                            </div>

                            {/* Files & Documents */}
                            {(selectedInfringement.pdf_file_path || selectedInfringement.printout_image_path) && (
                                <div style={{
                                    background: colors.bg,
                                    padding: '16px',
                                    borderRadius: '8px',
                                    border: `1px solid ${colors.border}20`
                                }}>
                                    <h4 style={{ color: colors.blue, marginBottom: '12px' }}>📄 Documents</h4>
                                    {selectedInfringement.pdf_file_path && (
                                        <div style={{ margin: '8px 0' }}>
                                            <p style={{ color: colors.fg, margin: '4px 0' }}>
                                                <strong>PDF File:</strong> {selectedInfringement.pdf_file_name || 'Available'}
                                            </p>
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        setLoading(true);
                                                        const response = await getInfringementFile(selectedInfringement.id, 'pdf');
                                                        if (response.success) {
                                                            setCurrentPdfUrl(response.file_url);
                                                            setSelectedInfringement(null); // Close the details modal first
                                                            setPdfViewerOpen(true);
                                                        } else {
                                                            showAlert({
                                                                message: response.error || 'Failed to load PDF',
                                                                type: 'danger',
                                                                buttons: [{ label: 'Close', type: 'primary' }]
                                                            });
                                                        }
                                                    } catch (error) {
                                                        showAlert({
                                                            message: 'Error loading PDF',
                                                            type: 'danger',
                                                            buttons: [{ label: 'Close', type: 'primary' }]
                                                        });
                                                    } finally {
                                                        setLoading(false);
                                                    }
                                                }}
                                                style={{
                                                    background: colors.blue,
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '8px 16px',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    marginTop: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faFileAlt} style={{ width: 30 }} /> View PDF
                                            </button>
                                            {/* Download Signed PDF Button - Only show for signed infringements and if canEdit is True */}
                                            {canEdit && (selectedInfringement.status === 'signed_off' || selectedInfringement.status === 'acknowledged') && 
                                             selectedInfringement.mobile_signature_data && (
                                                <button
                                                    onClick={async () => {
                                                        try {
                                                            setLoading(true);
                                                            console.log('📥 Downloading infringement from details:', selectedInfringement.id);
                                                            
                                                            // Download the PDF
                                                            const response = await downloadSignedPDF(selectedInfringement.id);
                                                            
                                                            if (response.success) {
                                                                // Update the downloaded status to true
                                                                const updateResult = await updateInfringement(selectedInfringement.id, { downloaded: true });
                                                                
                                                                if (updateResult.success) {
                                                                    // Close the modal and refresh the infringements
                                                                    setSelectedInfringement(null);
                                                                    await fetchInfringements();
                                                                    
                                                                    showAlert({
                                                                        message: 'Signed PDF downloaded successfully and marked as downloaded!',
                                                                        type: 'success',
                                                                        buttons: [{ label: 'Close', type: 'primary' }]
                                                                    });
                                                                } else {
                                                                    showAlert({
                                                                        message: 'Downloaded but failed to update status: ' + (updateResult.error || 'Unknown error'),
                                                                        type: 'warning',
                                                                        buttons: [{ label: 'Close', type: 'primary' }]
                                                                    });
                                                                }
                                                            } else {
                                                                showAlert({
                                                                    message: response.error || 'Failed to download signed PDF',
                                                                    type: 'danger',
                                                                    buttons: [{ label: 'Close', type: 'primary' }]
                                                                });
                                                            }
                                                        } catch (error) {
                                                            console.error('💥 Download Error:', error);
                                                            showAlert({
                                                                message: 'Error downloading signed PDF: ' + error.message,
                                                                type: 'danger',
                                                                buttons: [{ label: 'Close', type: 'primary' }]
                                                            });
                                                        } finally {
                                                            setLoading(false);
                                                        }
                                                    }}
                                                    style={{
                                                        background: colors.success,
                                                        color: 'white',
                                                        border: 'none',
                                                        padding: '8px 16px',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '12px',
                                                        marginTop: '8px',
                                                        marginLeft: '0px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faDownload} style={{ width: 30 }} /> Download
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {selectedInfringement.printout_image_path && (
                                        <div style={{ margin: '8px 0' }}>
                                            <p style={{ color: colors.fg, margin: '4px 0' }}>
                                                <strong>Printout Image:</strong> {selectedInfringement.printout_image_name || 'Available'}
                                            </p>
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        const result = await getInfringementFile(selectedInfringement.id, 'printout');
                                                        if (result.success) {
                                                            setCurrentImageUrl(result.file_url);
                                                            setImageViewerType('printout');
                                                            setSelectedInfringement(null);
                                                            setImageViewerOpen(true);
                                                        } else {
                                                            showAlert({
                                                                message: result.error || 'Failed to load printout image',
                                                                type: 'danger',
                                                                buttons: [{ label: 'Close', type: 'primary' }]
                                                            });
                                                        }
                                                    } catch (error) {
                                                        showAlert({
                                                            message: 'Error loading printout image',
                                                            type: 'danger',
                                                            buttons: [{ label: 'Close', type: 'primary' }]
                                                        });
                                                    }
                                                }}
                                                style={{
                                                    background: colors.blue,
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '8px 16px',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    marginTop: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faFileAlt} style={{ width: 30 }} /> View Printout
                                            </button>
                                            {canEdit && (
                                                <button
                                                    onClick={async () => {
                                                        try {
                                                            setLoading(true);
                                                            const response = await downloadPrintout(selectedInfringement.id);
                                                            if (response.success) {
                                                                showAlert({
                                                                    message: 'Printout downloaded successfully!',
                                                                    type: 'success',
                                                                    buttons: [{ label: 'Close', type: 'primary' }]
                                                                });
                                                            } else {
                                                                showAlert({
                                                                    message: response.error || 'Failed to download printout',
                                                                    type: 'danger',
                                                                    buttons: [{ label: 'Close', type: 'primary' }]
                                                                });
                                                            }
                                                        } catch (error) {
                                                            showAlert({
                                                                message: 'Error downloading printout',
                                                                type: 'danger',
                                                                buttons: [{ label: 'Close', type: 'primary' }]
                                                            });
                                                        } finally {
                                                            setLoading(false);
                                                        }
                                                    }}
                                                    style={{
                                                        background: colors.success,
                                                        color: 'white',
                                                        border: 'none',
                                                        padding: '8px 16px',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '12px',
                                                        marginTop: '8px',
                                                        marginLeft: '0px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faDownload} style={{ width: 30 }} /> Download
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    <p style={{ color: colors.muted, margin: '4px 0', fontSize: '12px' }}>
                                        <strong>Printout Required:</strong> {selectedInfringement.printout_required ? 'Yes' : 'No'}
                                    </p>
                                </div>
                            )}

                            {/* Status Information */}
                            <div style={{
                                background: colors.bg,
                                padding: '16px',
                                borderRadius: '8px',
                                border: `1px solid ${colors.border}20`
                            }}>
                                <h4 style={{ color: colors.blue, marginBottom: '12px' }}>📅 Timeline</h4>
                                <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        <p style={{ color: colors.fg, margin: '4px 0' }}>
                                            <strong>Created:</strong> {formatDate(selectedInfringement.created_at)}
                                        </p>
                                        {selectedInfringement.acknowledged_at && (
                                            <p style={{ color: colors.fg, margin: '4px 0' }}>
                                                <strong>Acknowledged:</strong> {formatDate(selectedInfringement.acknowledged_at)}
                                            </p>
                                        )}
                                        {selectedInfringement.signed_at && (
                                            <p style={{ color: colors.fg, margin: '4px 0' }}>
                                                <strong>Signed Off:</strong> {formatDate(selectedInfringement.signed_at)}
                                            </p>
                                        )}
                                    </div>
                                    {selectedInfringement.mobile_signature_data && (
                                        <div style={{ 
                                            background: '#f5f5f5',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '2px solid ' + colors.border,
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => {
                                            setCurrentImageUrl(selectedInfringement.mobile_signature_data);
                                            setImageViewerType('signature');
                                            setSelectedInfringement(null);
                                            setImageViewerOpen(true);
                                        }}
                                        >
                                            <img 
                                                src={selectedInfringement.mobile_signature_data}
                                                alt="Signature"
                                                style={{
                                                    maxWidth: '200px',
                                                    maxHeight: '100px',
                                                    display: 'block'
                                                }}
                                            />
                                            <p style={{ 
                                                color: colors.muted, 
                                                fontSize: '11px', 
                                                margin: '4px 0 0 0',
                                                textAlign: 'center'
                                            }}>
                                                Click to enlarge
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div style={{ marginTop: '24px', textAlign: 'center' }}>
                            <button
                                onClick={() => setSelectedInfringement(null)}
                                style={{
                                    background: colors.blue,
                                    color: 'white',
                                    border: 'none',
                                    padding: '12px 24px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    cursor: 'pointer'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* PDF Viewer Modal */}
            {pdfViewerOpen && currentPdfUrl && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000,
                    padding: '20px'
                }}>
                    <div style={{
                        background: colors.panel,
                        borderRadius: '12px',
                        width: '50%',
                        height: '85vh',
                        border: `1px solid ${colors.border}20`
                    }}>
                        <TachoPDFViewer pdfUrl={currentPdfUrl} show={setPdfViewerOpen} theme={theme} />
                    </div>
                </div>
            )}

            {/* Signature Image Viewer Modal */}
            {imageViewerOpen && currentImageUrl && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000,
                    padding: '20px'
                }}>
                    <div style={{
                        background: colors.panel,
                        borderRadius: '12px',
                        padding: '20px',
                        maxWidth: '90%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        border: `1px solid ${colors.border}20`,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ color: colors.blue, margin: 0 }}>
                                {imageViewerType === 'signature' ? '✍️ Mobile Signature' : '📄 Printout Image'}
                            </h3>
                            <button
                                onClick={() => {
                                    setImageViewerOpen(false);
                                    setCurrentImageUrl(null);
                                }}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: colors.fg,
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div style={{
                            background: '#f5f5f5',
                            padding: '20px',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <img 
                                src={currentImageUrl} 
                                alt={imageViewerType === 'signature' ? 'Mobile Signature' : 'Printout Image'}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '70vh',
                                    border: '2px solid ' + colors.border,
                                    borderRadius: '4px'
                                }}
                            />
                        </div>
                        <button
                            onClick={() => {
                                setImageViewerOpen(false);
                                setCurrentImageUrl(null);
                            }}
                            style={{
                                background: colors.blue,
                                color: 'white',
                                border: 'none',
                                padding: '12px 24px',
                                borderRadius: '6px',
                                fontSize: '14px',
                                cursor: 'pointer',
                                alignSelf: 'center'
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TachoManagement;